import { FC, useMemo, useCallback } from 'react'
import styles from 'components/features/pfm/OpportunityReviewComposePane/OpportunityReviewComposePane.styl'
import { OpportunityReviewCompose } from 'components/features/pfm/OpportunityReviewCompose/OpportunityReviewCompose'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import Button from 'components/features/pfm/Button/Button'
import { FloatingPane, OpportunityReviewFloatingPane } from 'interfaces/floatingPanes'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { composeValidateActionCreator } from 'actions/feedComposeActions'
import { createInsightBySocialInsightSelector } from 'selectors/socialInsightSelectors'
import { closeReviewComposePaneActionCreator } from 'actions/socialInsightsActions'
import { createFeedAllCommentUidsByPostUidSelector } from 'selectors/feedSelectors'
import { getCreateReviewComposeId } from 'helpers/composeHelpers'
import { ComposeSubmitButton } from 'components/features/socialFeed/ComposeSubmitButton/ComposeSubmitButton'

const TYPED_PANE: Pick<FloatingPane, 'type'> = {
  type: 'opportunity-review',
}

type OpportunityReviewComposePaneProps = {

}

export const OpportunityReviewComposePane: FC<OpportunityReviewComposePaneProps> = (props) => {
  const {} = props


  const paneSelector = useMemo(createFloatingPaneByTypeSelector, [])
  const pane = useStoreSelector((state) => paneSelector(state, TYPED_PANE)) as OpportunityReviewFloatingPane | null
  const uid = pane ? pane.payload.uid : ''
  const commentUid = pane && pane.payload.commentUid || undefined

  const postUidObj = useMemo(() => ({ postUid: uid }), [uid])
  const insightSelector = useMemo(createInsightBySocialInsightSelector, [])
  const insight = useStoreSelector((state) => insightSelector(state, postUidObj))

  const commentUidsSelector = useMemo(createFeedAllCommentUidsByPostUidSelector, [])
  const commentUids = useStoreSelector((state) => commentUidsSelector(state, postUidObj))
  const cancelButtonTitle = useMemo(() => {
    if (!commentUids || commentUids.results.length <= 1) {
      return 'Close'
    }

    return 'Cancel'
  }, [commentUids])

  const dispatch = useStoreDispatch()
  const handleSubmit = useCallback(() => {
    if (!pane) {
      return
    }

    dispatch(composeValidateActionCreator.request({
      composeId: `review${ pane.payload.uid }`,
    }))
  }, [dispatch, pane])

  const handleClose = useCallback(() => {
    dispatch(closeReviewComposePaneActionCreator())
  }, [dispatch])

  const advertiserChild = useMemo(() => {
    const prefix = 'Review this opportunity'
    const title = insight && insight.advertiser ? insight.advertiser.name : null

    if (!title) {
      return (
        <div className={ styles.title }>{ prefix }</div>
      )
    }

    return (
      <>
        <div className={ styles.title }>{ prefix } from:</div>
        <div className={ styles.subTitle }>{ title }</div>
      </>
    )
  }, [insight])

  const footer = useMemo(() => (
    <>
      <div className={ styles.saveReview }>
        <ComposeSubmitButton
          composeId={ getCreateReviewComposeId(uid) }
          size='medium'
          text='Save Review'
          qaMarker='save-button'
          fullWidth
          onClick={ handleSubmit }
        />
      </div>
      <Button
        size='medium'
        style='hollow'
        text={ cancelButtonTitle }
        qaMarker='save-button'
        fullWidth
        onClick={ handleClose }
      />
    </>
  ), [cancelButtonTitle, handleSubmit, handleClose, uid])

  return useMemo(() => {
    if (!pane) {
      return null
    }

    return (
      <FloatingPaneLayout
        type='opportunity-review'
        footer={ footer }
        onClose={ handleClose }
      >
        <div className={ styles.opportunityReviewComposePane }>
          { advertiserChild }
          <div className={ styles.compose }>
            <OpportunityReviewCompose
              socialInsightUid={ pane.payload.uid }
              commentUid={ commentUid }
            />
          </div>
        </div>
      </FloatingPaneLayout>
    )
  }, [pane, footer, handleClose, advertiserChild, commentUid])
}
